import Authorizer from '$lib/services/auth';
import { AnalyticsService } from '$lib/services/analytics';
import { startLoading, stopLoading } from '$lib/stores/app/actions';
import { app } from '$lib/stores/app/store';
import { get } from 'svelte/store';
import { resolveCondition } from './resolve-condition';
import { Sentry } from '$lib/services/sentry';
import { AppInitializationService } from '$lib/services/app-initialization';
import { SENTRY_ERROR_LEVELS } from './constants';

/**
 * @typedef {object} SchemaOrgPublisher
 * @property {string} id - The ID of the publisher for schema.org publisher fields
 */

/**
 * @typedef {object} SchemaOrgImageObject
 * @property {string} type - The type of the image
 * @property {string} url - The URL of the image
 */

/**
 * @typedef {object} SchemaOrgIsPartOf
 * @property {string} id - The ID of the isPartOf for schema.org isPartOf fields
 */

/**
 * @typedef {object} SchemaOrgOrganization
 * @property {string} [id] - The id of the schema
 * @property {SchemaOrgImageObject} [logo] - The logo of the organization
 * @property {string} [name] - The name of the organization
 * @property {Array<string>} [sameAs] - The sameAs of the organization
 * @property {string} [type] - The type of the schema
 * @property {string} [url] - The URL of the organization
 */

/**
 * @typedef {object} SchemaOrgWebPage
 * @property {string} [description] - The description of the web page
 * @property {string} [id] - The ID of the schema
 * @property {string} [inLanguage] - The language of the web page
 * @property {SchemaOrgIsPartOf} [isPartOf] - The isPartOf of the web page
 * @property {string} [name] - The name of the web page
 * @property {SchemaOrgImageObject} [primaryImageOfPage] - The primaryImageOfPage of the web page
 * @property {SchemaOrgPublisher} [publisher] - The publisher of the web page
 * @property {string} [type] - The type of the schema
 * @property {string} [url] - The URL of the web page
 */

/**
 * @typedef {object} SchemaOrgWebSite
 * @property {string} [id] - The id of the web site
 * @property {string} [name] - The name of the website
 * @property {SchemaOrgPublisher} [publisher] - The publisher of the website
 * @property {string} [type] - The type of the schema
 * @property {string} [url] - The URL of the website
 */

/**
 * @typedef {object} SchemaOrgGraph
 * @property {SchemaOrgOrganization} [organization] - The organization of the graph
 * @property {SchemaOrgWebPage} [webPage] - The web page of the graph
 * @property {SchemaOrgWebSite} [webSite] - The web site of the graph
 */

/**
 * @typedef {object} SchemaOrgSchema
 * @property {string} [context] - The context of the schema
 * @property {SchemaOrgGraph} [graph] - The graph of the schema
 */

/**
 * @typedef {object} LinkTag
 * @property {string} href - The href attribute of the link tag
 * @property {string} [hreflang] - The hreflang attribute of the link tag
 * @property {string} rel - The rel attribute of the link tag
 */

/**
 * @typedef {object} MetaTag
 * @property {string} content - The content attribute of the meta tag
 * @property {string} [href] - The href attribute of the meta tag
 * @property {string} [name] - The name attribute of the meta tag
 * @property {string} [property] - The property attribute of the meta tag
 * @property {string} [pageVariant] - The page variant attribute of the meta tag
 */

/**
 * @typedef {object} PageMetadata
 * @property {Array<LinkTag>} [link] - Array of link objects
 * @property {Array<MetaTag>} [metaTag] - Array of meta tag objects
 * @property {SchemaOrgSchema} [schemaOrg] - The schema.org schema of the page
 * @property {string} [schemaOrgJson] - The schema.org JSON of the page
 * @property {string} [title] - The page title
 */

/**
 * @typedef {object} PageCondition
 * @property {(config: import('../stores/app/types').App.AppStore) => Promise<boolean|null>} condition - Function that evaluates if condition is met
 * @property {() => Promise<any>} resolve - Function that resolves to a module
 */

/**
 * @typedef {object} PolicyObject
 * @property {() => Promise<boolean>} policy - Function that returns a boolean indicating if access is allowed
 * @property {() => Promise<string>} [policyExceptionHandler] - Function that returns redirect path when policy fails
 */

/**
 * @typedef {object} PageOptions
 * @property {string} [routePath='/'] - The route ID of the page
 * @property {PageMetadata} [metadata={}] - The metadata for the page
 * @property {boolean} [requireAuth=true] - Whether the page requires authentication
 * @property {PolicyObject} [policies={}] - Authorization policies
 * @property {Record<string, PageCondition>} [conditions={}] - Page conditions
 */

export class Page {
	/**
	 * @type {SchemaOrgSchema}
	 */
	static defaultSchemaOrg = {
		context: 'https://schema.org',
		graph: {
			organization: {
				id: 'https://www.abcmouse.com/#organization',
				logo: {
					type: 'ImageObject',
					url: 'https://img.abcmouse.com/fb/ABCmouse.jpg',
				},
				name: 'ABCmouse',
				sameAs: [
					'https://www.youtube.com/abcmouse',
					'https://www.tiktok.com/@abcmouse',
					'https://www.facebook.com/ABCmouse',
					'https://www.instagram.com/abcmouse',
					'https://www.pinterest.com/abcmouse/',
				],
				type: 'Organization',
				url: 'https://www.abcmouse.com/',
			},
			webPage: {
				description:
					'ABCmouse is the leading online early learning program for kids ages 2–8, offering a complete curriculum in reading, math, science, and more.',
				id: 'https://www.abcmouse.com/abc/#webpage',
				inLanguage: 'en',
				isPartOf: {
					id: 'https://www.abcmouse.com/#website',
				},
				name: 'ABCmouse: Online Learning for Ages 2–8',
				primaryImageOfPage: {
					type: 'ImageObject',
					url: 'https://img.abcmouse.com/fb/ABCmouse.jpg',
				},
				publisher: {
					id: 'https://www.abcmouse.com/#organization',
				},
				type: 'WebPage',
				url: 'https://www.abcmouse.com/abc/',
			},
			webSite: {
				id: 'https://www.abcmouse.com/#website',
				name: 'ABCmouse',
				publisher: {
					id: 'https://www.abcmouse.com/#organization',
				},
				type: 'WebSite',
				url: 'https://www.abcmouse.com/',
			},
		},
	};

	/**
	 * @type {PageMetadata}
	 */
	metadata = {
		link: [],
		metaTag: [
			{
				content:
					'ABCmouse.com helps kids learn to read through phonics, and teaches lessons in math, social studies, art, music, and much more. For kids age 2 to Kindergarten.',
				name: 'description',
			},
			{
				content: 'summary_large_image',
				name: 'twitter:card',
			},
			{
				content: '@ABCmouse',
				name: 'twitter:creator',
			},
			{
				content:
					'ABCmouse is the award-winning learning app for young children. Discover a step-by-step learning path for reading, math, science, and art.',
				name: 'twitter:description',
			},
			{
				content: 'https://www.abcmouse.com/learn/wp-content/uploads/2023/07/ABCm_Devices.png',
				name: 'twitter:image',
			},
			{
				content: '@ABCmouse',
				name: 'twitter:site',
			},
			{
				content: 'ABCmouse: Online Learning for Ages 2–8',
				name: 'twitter:title',
			},
			{
				content:
					'ABCmouse is the leading online early learning program for kids ages 2–8, offering a complete curriculum in reading, math, science, and more.',
				property: 'og:description',
			},
			{
				content: 'https://img.abcmouse.com/fb/ABCmouse.jpg',
				property: 'og:image',
			},
			{
				content: 'en_US',
				property: 'og:locale',
			},
			{
				content: 'ABCmouse: Online Learning for Ages 2–8',
				property: 'og:title',
			},
			{
				content: 'website',
				property: 'og:type',
			},
		],
		schemaOrg: Page.defaultSchemaOrg,
		schemaOrgJson: '',
		title: 'ABCmouse: Educational Games, Books, Puzzles & Songs for Kids & Toddlers',
	};

	/**
	 * Creates a new Page instance
	 * @param {PageOptions} options - The page configuration options
	 */
	constructor({
		routePath = '/',
		metadata = {},
		requireAuth = true,
		policies = { policy: async () => true, policyExceptionHandler: async () => '/' },
		conditions = {},
	}) {
		this.routePath = routePath;
		this.requireAuth = requireAuth;
		this.policies = policies;
		this.conditions = conditions;

		this.resolveMetadata({ routePath, metadata });
	}

	/**
	 * Resolve the page metadata and set the metadata and schema.org schema on the page instance
	 * @param {object} options - The options for the metadata
	 * @param {string} options.routePath - The route path of the page
	 * @param {PageMetadata} options.metadata - The metadata for the page
	 * @returns {void}
	 */
	resolveMetadata({ routePath = '/', metadata = {} }) {
		this.metadata.title = metadata?.title ? metadata.title : this.metadata.title;
		this.resolveMetadataLink({ routePath, link: metadata?.link ?? [] });
		this.resolveMetadataMetaTag(metadata?.metaTag ?? []);
		this.resolveMetadataSchemaOrgJson(metadata?.schemaOrg?.graph ?? {});
	}

	/**
	 * Build the schema.org JSON for the page
	 * @param {SchemaOrgGraph} schemaOrgGraph - The schema.org graph for the page
	 * @returns {void}
	 */
	resolveMetadataSchemaOrgJson(schemaOrgGraph = {}) {
		const schemaOrg = {
			'@context': Page.defaultSchemaOrg.context,
			'@graph': [
				this.resolveMetadataSchemaOrgGraphOrganization(schemaOrgGraph?.organization ?? {}),
				this.resolveMetadataSchemaOrgGraphWebPage(schemaOrgGraph?.webPage ?? {}),
				this.resolveMetadataSchemaOrgGraphWebSite(schemaOrgGraph?.webSite ?? {}),
			],
		};
		this.metadata.schemaOrgJson = JSON.stringify(schemaOrg);
	}

	/**
	 * Get the schema.org organization object prepared for the schema.org JSON
	 * @param {SchemaOrgOrganization} organization - The schema.org organization object
	 * @returns {object} - The schema.org organization object prepared for the schema.org JSON
	 */
	resolveMetadataSchemaOrgGraphOrganization(organization = {}) {
		return {
			'@id': organization?.id ?? Page.defaultSchemaOrg?.graph?.organization?.id,
			'logo': {
				'@type': organization?.logo?.type ?? Page.defaultSchemaOrg?.graph?.organization?.logo?.type,
				'url': organization?.logo?.url ?? Page.defaultSchemaOrg?.graph?.organization?.logo?.url,
			},
			'name': organization?.name ?? Page.defaultSchemaOrg?.graph?.organization?.name,
			'sameAs': organization?.sameAs ?? Page.defaultSchemaOrg?.graph?.organization?.sameAs,
			'@type': 'Organization', // not mutable
			'url': organization?.url ?? Page.defaultSchemaOrg?.graph?.organization?.url,
		};
	}

	/**
	 * Get the schema.org web page object prepared for the schema.org JSON
	 * @param {SchemaOrgWebPage} webPage - The schema.org web page object
	 * @returns {object} - The schema.org web page object prepared for the schema.org JSON
	 */
	resolveMetadataSchemaOrgGraphWebPage(webPage = {}) {
		return {
			'description': webPage?.description ?? Page.defaultSchemaOrg?.graph?.webPage?.description,
			'@id': webPage?.id ?? Page.defaultSchemaOrg?.graph?.webPage?.id,
			'inLanguage': webPage?.inLanguage ?? Page.defaultSchemaOrg?.graph?.webPage?.inLanguage,
			'isPartOf': {
				'@id': webPage?.isPartOf?.id ?? Page.defaultSchemaOrg?.graph?.webPage?.isPartOf?.id,
			},
			'name': webPage?.name ?? Page.defaultSchemaOrg?.graph?.webPage?.name,
			'primaryImageOfPage': {
				'@type':
					webPage?.primaryImageOfPage?.type ??
					Page.defaultSchemaOrg?.graph?.webPage?.primaryImageOfPage?.type,
				'url':
					webPage?.primaryImageOfPage?.url ??
					Page.defaultSchemaOrg?.graph?.webPage?.primaryImageOfPage?.url,
			},
			'publisher': {
				'@id': webPage?.publisher?.id ?? Page.defaultSchemaOrg?.graph?.webPage?.publisher?.id,
			},
			'@type': 'WebPage', // not mutable
			'url': webPage?.url ?? Page.defaultSchemaOrg?.graph?.webPage?.url,
		};
	}

	/**
	 * Get the schema.org web site object prepared for the schema.org JSON
	 * @param {SchemaOrgWebSite} webSite - The schema.org web site object
	 * @returns {object} - The schema.org web site object prepared for the schema.org JSON
	 */
	resolveMetadataSchemaOrgGraphWebSite(webSite = {}) {
		return {
			'@id': webSite?.id ?? Page.defaultSchemaOrg?.graph?.webSite?.id,
			'name': webSite?.name ?? Page.defaultSchemaOrg?.graph?.webSite?.name,
			'publisher': {
				'@id': webSite?.publisher?.id ?? Page.defaultSchemaOrg?.graph?.webSite?.publisher?.id,
			},
			'@type': 'WebSite', // not mutable
			'url': webSite?.url ?? Page.defaultSchemaOrg?.graph?.webSite?.url,
		};
	}

	/**
	 * Resolve the meta tag metadata for the page, ensuring that there are no duplicate meta tags
	 * @param {MetaTag[]} metaTags - The meta tag metadata for the page
	 * @returns {void}
	 */
	resolveMetadataMetaTag(metaTags = []) {
		this.metadata.metaTag = /** @type {MetaTag[]} */ (
			this.mergeUniqueTags(this.metadata.metaTag ?? [], metaTags)
		);
	}

	/**
	 * Resolve the link metadata for the page, ensuring exactly one canonical link is present.
	 * @param {object} options - The options for the metadata links
	 * @param {string} options.routePath - The route path of the page
	 * @param {LinkTag[]} options.link - The link metadata for the page
	 * @returns {void}
	 */
	resolveMetadataLink({ routePath = '/', link = [] }) {
		/** @type {LinkTag[]} */
		let canonicalLink = [
			{
				rel: 'canonical',
				href: `https://www.abcmouse.com${routePath}`,
			},
		];
		/** @type {LinkTag[]} */
		let allLinks = [];
		link.forEach((linkTag) => {
			if (linkTag.rel === 'canonical') {
				// there can only be one canonical link!
				canonicalLink = /** @type {LinkTag[]} */ (this.mergeUniqueTags(canonicalLink, [linkTag]));
			} else {
				// other rel can have multiple tags for the same rel
				allLinks.push(linkTag);
			}
		});
		this.metadata.link = [...canonicalLink, ...allLinks];
	}

	/**
	 * Merge two arrays together, ensuring that there are no duplicate tags for properties that should not be duplicated.
	 * Overwrite the first tag set with the second tag set if the identifier has the same value.
	 * The identifier is the name, property, or rel attribute of the tag.
	 * This is for tags where the same identifier should not be duplicated (like canonical links)
	 * @param {LinkTag[]|MetaTag[]} tagSet1 - The first tag set to merge
	 * @param {LinkTag[]|MetaTag[]} tagSet2 - The second tag set to merge
	 * @returns {LinkTag[]|MetaTag[]} - The merged tag set, presum
	 */
	mergeUniqueTags(tagSet1, tagSet2) {
		/**
		 * Resolve the name of the tag key to use for the map
		 * @param {LinkTag|MetaTag} tag - The tag to resolve the key for
		 * @returns {string} - The key to use for the map
		 */
		const getTagKey = (tag) => {
			let key = '';
			if ('name' in tag && tag.name) {
				key = tag.name;
			} else if ('property' in tag && tag.property) {
				key = tag.property;
			} else if ('rel' in tag && tag.rel) {
				key = tag.rel;
			}
			return key;
		};
		const mergedMap = new Map();
		tagSet1.forEach((tag) => mergedMap.set(getTagKey(tag), tag));
		tagSet2.forEach((tag) => mergedMap.set(getTagKey(tag), tag));
		return Array.from(mergedMap.values());
	}

	/**
	 * Handles authorization for the page
	 * @returns {Promise<void>} Promise that resolves when the authorization is handled
	 */
	async handleAuthorization() {
		if (!this.requireAuth) return;

		startLoading({ darkMode: true });
		try {
			const authorizer = new Authorizer({
				policies: this.policies,
				routePath: this.routePath,
			});
			return await authorizer.browserAuthorize();
		} catch (error) {
			AnalyticsService.trackClientDebug({
				eventSubType: 'page-authorization-error',
				source: 'Page',
				error: error,
			});
		} finally {
			stopLoading();
		}
	}

	/**
	 * Resolves the appropriate page component based on conditions
	 * @returns {Promise<{ module: typeof import('svelte').SvelteComponent, name: string }>} The resolved page component class
	 * @throws {Error} When no conditions are met and no default condition exists
	 */
	resolvePageCondition = async () => {
		const appMeta = get(app);
		const condition = await resolveCondition(this.conditions, appMeta);
		return condition;
	};

	/**
	 * Handles analytics tracking after navigation
	 * @param {object} options - The options for the analytics
	 * @param {string} options.componentName - The name of the component
	 * @returns {Promise<void>}
	 */
	async handleAnalytics({ componentName }) {
		await AnalyticsService.afterNavigate({ componentName });
	}

	/**
	 * Add the page name to the metadata
	 * @param {string} variant - The variant of the page
	 * @returns {void}
	 */
	addPageVariantToMetadata(variant) {
		if (this.metadata.metaTag) {
			this.metadata.metaTag.forEach((tag) => {
				tag.pageVariant = variant;
			});
		}
	}

	/**
	 * Initializes the page by handling authorization, resolving conditions, and setting up analytics
	 * @returns {Promise<typeof import('svelte').SvelteComponent|undefined>} The resolved page component class or undefined if the page is not authorized
	 */
	async init() {
		try {
			// Wait for hooks.client.js initialization to complete before executing any page logic
			// This ensures global validateUser runs before any policy validation
			await AppInitializationService.waitForInitialization();

			const isAuthorized = await this.handleAuthorization();
			//TODO: hack to handle the case where the page is not authorized and the requireAuth flag is true, cleanup when requireAuth is removed
			if (!isAuthorized && this.requireAuth) return;

			const { module, name } = await this.resolvePageCondition();
			this.addPageVariantToMetadata(name);
			await this.handleAnalytics({ componentName: name });
			return module.default;
		} catch (error) {
			Sentry.captureMessage('Error: Page.init', {
				level: SENTRY_ERROR_LEVELS.FATAL,
				extra: {
					error: error,
				},
			});
		}
	}
}
